import React, { useMemo, useState, useEffect } from "react";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";
import { showToast } from "@/api/notification";
import { Alert } from "react-bootstrap";
import Loading from "./Loading";

/** Types */
export type CategoryNode = {
  category_id: string;
  name: string;
  slug?: string;
  order_number?: string;
  index?: number;
  cover_image?: string | null;
  is_active?: boolean;
  subcategories?: CategoryNode[];
};

type RenderRow = {
  level1: CategoryNode;
  showLevel1: boolean;
  level1RowSpan: number;
  level2: CategoryNode | null;
  showLevel2: boolean;
  level2RowSpan: number;
  level3: CategoryNode | null;
};

interface ApiResponse {
  message: string;
  error_status: boolean;
  status: number;
  data: {
    Apparel: CategoryNode[];
    MSR: CategoryNode[];
  };
}

interface rootCategory {
  category_id: string;
  name: string;
  order_number?: string;
  slug?: string;
}
interface CategoryTableProps {
  onEdit?: (category: any) => void;
  onAddCategory?: () => void;
}

const CategoryHierarchyTable: React.FC<CategoryTableProps> = () => {
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const { getApi, putApi, postApi, deleteApi } = useGeneralApiCall();
  const [tableData, setTableData] = useState<CategoryNode[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState<CategoryNode | null>(
    null
  );

  const [editName, setEditName] = useState("");

  // craete a CATEGORY
  const [showCategoryModel, setCategoryModel] = useState(false);
  const [rootcategory, setRootCategory] = useState("");
  const [createChildCategory, setCareteChildCategory] = useState("");
  const [setParentCategory, setParesntCategory] = useState("");
  const [showRootCategory, setShowParentCategory] = useState<rootCategory[]>();
  const [showRoot, setShowRoot] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState("");

  const getData = async () => {
    setLoading(true);
    try {
      const response: ApiResponse = await getApi(
        "/api/msr/v1/combined-categories/"
      );
      const response1: any = await getApi("/api/cat/v1/list-root-categories/");
      if (response?.status === 200 && response?.data) {
        setTableData(response?.data?.Apparel?.reverse() || []);
      }
      if (response1?.status === 200 && response1?.data) {
        setShowParentCategory(response1?.data || []);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const createRootCategory = async () => {
    // Add your edit API call here
    setCategoryLoading(true);

    try {
      const response = await postApi("/api/cat/v1/categories/", {
        name: rootcategory,
        is_active: "true",
      });

      if (response.status === 201) {
        showToast({ type: "success", message: response.message });
        setCategoryModel(false);
        setRootCategory("");
        getData();
      }
    } catch (error: any) {
      let errorMessage = "An error occurred while Update the category";

      if (error?.response?.data?.errors_data?.error) {
        // This extracts: "Cannot delete category with subcategories."
        errorMessage = error.response.data.errors_data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setCategoryError(errorMessage);
    } finally {
      setCategoryLoading(false);
    }
  };
  const createSabCategory = async () => {
    // Add your edit API call here
    setCategoryLoading(true);

    try {
      const response = await postApi("api/cat/v1/create-sub-category/", {
        name: createChildCategory,
        parent_category: setParentCategory,
      });

      if (response.status === 201) {
        showToast({ type: "success", message: response.message });
        setCategoryModel(false);
        setCareteChildCategory("");
        setParesntCategory("");
        getData();
      }
    } catch (error: any) {
      let errorMessage = "An error occurred while Update the category";

      if (error?.response?.data?.errors_data?.error) {
        // This extracts: "Cannot delete category with subcategories."
        errorMessage = error.response.data.errors_data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setCategoryError(errorMessage);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleEdit = (category: CategoryNode | null) => {
    setSelectedCategory(category);
    setEditName(category?.name ?? "");
    setShowEditModal(true);
  };

  const handleDelete = (category: CategoryNode | null) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const confirmEdit = async () => {
    // Add your edit API call here
    setEditLoading(true);
    const postData = {
      name: editName,
      parent_category: selectedCategory?.category_id,
    };

    try {
      const response = await putApi(
        "/api/cat/v1/change-category-name",
        selectedCategory?.category_id ?? "",
        postData
      );

      if (response.status === 200) {
        showToast({ type: "success", message: response.message });
        setShowEditModal(false);
        setSelectedCategory(null);
        setEditName("");
        getData();
      }
    } catch (error: any) {
      let errorMessage = "An error occurred while Update the category";

      if (error?.response?.data?.errors_data?.error) {
        // This extracts: "Cannot delete category with subcategories."
        errorMessage = error.response.data.errors_data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setEditError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const response = await deleteApi(
        "/api/cat/v1/delete-category",
        selectedCategory?.category_id
      );

      if (response.status === 202) {
        showToast({ type: "success", message: response.message });
        getData();
        setShowDeleteModal(false);
        setSelectedCategory(null);
      }
    } catch (error: any) {
      // Fix: Extract the actual error message string
      let errorMessage = "An error occurred while deleting the category";

      if (error?.response?.data?.errors_data?.error) {
        // This extracts: "Cannot delete category with subcategories."
        errorMessage = error.response.data.errors_data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = "Cannot delete category with subcategories";
      }

      setDeleteError(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelModal = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedCategory(null);
    setEditName("");
    setDeleteError("");
    setEditError("");
  };

  const rows = useMemo<RenderRow[]>(() => {
    const result: RenderRow[] = [];

    tableData.forEach((level1) => {
      const level2List = level1.subcategories ?? [];

      if (level2List.length === 0) {
        result.push({
          level1,
          showLevel1: true,
          level1RowSpan: 1,
          level2: null,
          showLevel2: false,
          level2RowSpan: 0,
          level3: null,
        });
        return;
      }

      let level1TotalRows = 0;
      const level1StartIndex = result.length;

      level2List.forEach((level2) => {
        const level3List = level2.subcategories ?? [];
        const level2Rows = Math.max(1, level3List.length);

        if (level3List.length === 0) {
          result.push({
            level1,
            showLevel1: false,
            level1RowSpan: 0,
            level2,
            showLevel2: true,
            level2RowSpan: 1,
            level3: null,
          });
          level1TotalRows += 1;
        } else {
          level3List.forEach((level3, index) => {
            result.push({
              level1,
              showLevel1: false,
              level1RowSpan: 0,
              level2,
              showLevel2: index === 0,
              level2RowSpan: index === 0 ? level2Rows : 0,
              level3,
            });
          });
          level1TotalRows += level2Rows;
        }
      });

      if (result.length > level1StartIndex) {
        result[level1StartIndex].showLevel1 = true;
        result[level1StartIndex].level1RowSpan = level1TotalRows;
      }
    });

    return result;
  }, [tableData]);

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="w-full flex justify-end">
            <button
              type="button"
              className="px-3 py-2 mb-2 bg-emerald-400 text-white text-sm rounded"
              onClick={() => setCategoryModel(true)}
            >
              ADD Category
            </button>
          </div>

          <div className="relative">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr>
                  <th className="border-4 border-gray-300 p-6 text-center text-4xl">
                    Level 1
                  </th>
                  <th className="border-4 border-gray-300 p-6 text-center text-4xl">
                    Level 2
                  </th>
                  {/* <th className="border-4 border-gray-300 p-6 text-center text-4xl">
              Level 3
            </th> */}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    {row.showLevel1 && (
                      <td
                        rowSpan={row.level1RowSpan}
                        className="border-2 border-gray-300 relative "
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          fontSize: "30px",
                          fontWeight: "bold",
                        }}
                      >
                        <div className="mb-4">{row.level1.name}</div>

                        <div className="flex gap-2 justify-center mt-2">
                          <button
                            onClick={() => handleEdit(row.level1)}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(row.level1)}
                            className="px-3 py-1 bg-red-500 text-white text-sm rounded"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}

                    {row.showLevel2 && (
                      <td
                        rowSpan={row.level2RowSpan}
                        className="border-2 border-gray-300"
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          fontSize: "28px",
                          fontWeight: "500",
                        }}
                      >
                        <div className="mb-4">{row.level2?.name}</div>

                        <div className="flex gap-2 justify-center mt-2">
                          <button
                            onClick={() => handleEdit(row?.level2)}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded "
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(row?.level2)}
                            className="px-3 py-1 bg-red-500 text-white text-sm rounded "
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}

                    {/* <td
                className="border-2 border-gray-300"
                style={{
                  textAlign: "center",
                  padding: "12px",
                  fontSize: "15px",
                }}
              >
                {row.level3?.name || ""}
              </td> */}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Edit Modal */}
            {/* Edit Modal */}
            {showEditModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-96 max-w-md">
                  <h2 className="text-xl font-semibold mb-4">Edit Category</h2>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category Name
                    </label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter category name"
                    />
                  </div>
                  {editError && (
                    <div className="mb-4">
                      <Alert variant="danger">{editError}</Alert>
                    </div>
                  )}
                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={cancelModal} // Fixed: was setCategoryModel(false)
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded "
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmEdit}
                      disabled={!editName.trim() || editLoading}
                      className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {editLoading ? "Updating..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Category Modal */}
            {/* {showCategoryModel && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-[50%] max-w-md">
                  <h2 className="text-xl font-semibold mb-4">Category</h2>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowRoot(!showRoot)}
                    >
                      Craete Root category
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRoot(!showRoot)}
                    >
                      Craete Subcategory category
                    </button>
                  </div>

                  {showRoot && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Craete Root Category
                        </label>
                        <input
                          type="text"
                          value={rootcategory}
                          onChange={(e) => setRootCategory(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter category name"
                        />
                      </div>
                      {categoryError && <Alert>{categoryError}</Alert>}
                      <div className="flex gap-3 justify-end">
                        <button
                          type="button"
                          onClick={() => setCategoryModel(false)}
                          className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={createRootCategory}
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {categoryLoading ? "Submitting" : "Save Changes"}
                        </button>
                      </div>
                    </>
                  )}

                  {!showRoot && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Create Sub category
                        </label>
                        <input
                          type="text"
                          value={createChildCategory}
                          onChange={(e) =>
                            setCareteChildCategory(e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter category name"
                        />
                      </div>
                      <select value={setParentCategory}>
                        {showRootCategory?.map((item) => (
                          <option
                            onClick={() => setParesntCategory(item.category_id)}
                          >
                            {item.name}
                          </option>
                        ))}
                      </select>
                      {categoryError && <Alert>{categoryError}</Alert>}
                      <div className="flex gap-3 justify-end">
                        <button
                          type="button"
                          onClick={() => setCategoryModel(false)}
                          className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={createSabCategory}
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {categoryLoading ? "Submitting" : "Save Changes"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )} */}
            {/* Category Modal */}
            {showCategoryModel && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-[600px] max-w-[90vw]">
                  <h2 className="text-xl font-semibold mb-4">
                    {showRoot ? "Add category" : "Add Sub Category "}
                  </h2>

                  {/* Toggle Buttons */}
                  <div className="flex gap-3 mb-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowRoot(true);
                        setCategoryError("");
                      }}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        showRoot
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      Create Root Category
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRoot(false);
                        setCategoryError("");
                      }}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        !showRoot
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      Create Subcategory
                    </button>
                  </div>

                  {showRoot && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Create Root Category
                        </label>
                        <input
                          type="text"
                          value={rootcategory}
                          onChange={(e) => setRootCategory(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter category name"
                        />
                      </div>
                      {categoryError && (
                        <div className="mb-4">
                          <Alert variant="danger">{categoryError}</Alert>
                        </div>
                      )}
                      <div className="flex gap-3 justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryModel(false);
                            setCategoryError("");
                            setRootCategory("");
                          }}
                          className="px-4 py-2 text-gray-600 border border-gray-300 rounded"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={createRootCategory}
                          disabled={!rootcategory.trim() || categoryLoading}
                          className="px-4 py-2 bg-blue-500 text-white rounded cursor-pointer"
                        >
                          {categoryLoading ? "Creating..." : "Create Category"}
                        </button>
                      </div>
                    </>
                  )}

                  {!showRoot && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Create Subcategory
                        </label>
                        <input
                          type="text"
                          value={createChildCategory}
                          onChange={(e) =>
                            setCareteChildCategory(e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter subcategory name"
                        />
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Parent Category
                        </label>
                        <select
                          value={setParentCategory}
                          onChange={(e) => setParesntCategory(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select a parent category</option>
                          {showRootCategory?.map((item) => (
                            <option
                              key={item.category_id}
                              value={item.category_id}
                            >
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {categoryError && (
                        <div className="mb-4">
                          <Alert variant="danger">{categoryError}</Alert>
                        </div>
                      )}

                      <div className="flex gap-3 justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryModel(false);
                            setCategoryError("");
                            setCareteChildCategory("");
                            setParesntCategory("");
                          }}
                          className="px-4 py-2 text-gray-600 border border-gray-300 rounded "
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={createSabCategory}
                          disabled={
                            !createChildCategory.trim() ||
                            !setParentCategory ||
                            categoryLoading
                          }
                          className="px-4 py-2 bg-blue-500 text-white rounded cursor-pointer"
                        >
                          {categoryLoading
                            ? "Creating..."
                            : "Create Subcategory"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-96 max-w-md">
                  <h2 className="text-xl font-semibold mb-4 text-red-600">
                    Confirm Delete
                  </h2>
                  <p className="text-gray-700 mb-6">
                    Are you sure you want to delete the category "
                    {selectedCategory?.name}"? This action cannot be undone.
                  </p>
                  {deleteError && <Alert variant="danger">{deleteError}</Alert>}
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={cancelModal}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded "
                    >
                      Cancel
                    </button>

                    <button
                      onClick={confirmDelete}
                      className="px-4 py-2 bg-red-500 text-white rounded "
                    >
                      {deleteLoading ? "Submitting ..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default CategoryHierarchyTable;
